import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { SectionHelpTooltip } from "@/components/shared/SectionHelpTooltip";
import { useToast } from "@/hooks/use-toast";
import {
  OI_NOM_SCALES,
  formatOiNomScaleLabel,
  loadOiNomPrefs,
  enabledOiNomScaleList,
  OI_NOM_PREFS_EVENT,
  loadOiNomPanelPrefs,
  saveOiNomPanelPrefs,
  type OiNomPanelPrefs,
} from "@/lib/oi-nomenclature-sheets";
import {
  nomenclatureSheetsApi,
  type NomenclatureIntersectResponse,
  type NomenclatureSheetHit,
} from "@/services/nomenclature-sheets-api.service";
import { Download, GripVertical, Loader2, Search, X } from "lucide-react";
import { cn } from "@/lib/utils";

const PANEL_WIDTH = 352;
const PANEL_MARGIN = 16;
/** Отступ снизу, чтобы не перекрывать scale bar / координаты. */
const DEFAULT_BOTTOM_GAP = 96;

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onHighlight: (sheets: NomenclatureSheetHit[]) => void;
  onClearHighlight: () => void;
  onLocateObject?: (objectId: number) => void;
};

function defaultPosition(panelHeight = 320): { x: number; y: number } {
  const w = typeof window !== "undefined" ? window.innerWidth : 1280;
  const h = typeof window !== "undefined" ? window.innerHeight : 800;
  return {
    x: Math.max(PANEL_MARGIN, w - PANEL_WIDTH - PANEL_MARGIN),
    y: Math.max(PANEL_MARGIN, h - panelHeight - DEFAULT_BOTTOM_GAP),
  };
}

function clampPosition(x: number, y: number, width: number, height: number) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const maxX = Math.max(PANEL_MARGIN, vw - width - PANEL_MARGIN);
  const maxY = Math.max(PANEL_MARGIN, vh - height - PANEL_MARGIN);
  return {
    x: Math.min(maxX, Math.max(PANEL_MARGIN, x)),
    y: Math.min(maxY, Math.max(PANEL_MARGIN, y)),
  };
}

export function OiNomenclatureIntersectPanel({
  open,
  onOpenChange,
  onHighlight,
  onClearHighlight,
  onLocateObject,
}: Props) {
  const { toast } = useToast();
  const rootRef = useRef<HTMLDivElement | null>(null);
  const dragRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    origX: number;
    origY: number;
  } | null>(null);

  const [panelPrefs, setPanelPrefs] = useState<OiNomPanelPrefs>(() =>
    loadOiNomPanelPrefs(),
  );
  const [pos, setPos] = useState<{ x: number; y: number }>(() => {
    const p = loadOiNomPanelPrefs();
    if (p.x != null && p.y != null) return { x: p.x, y: p.y };
    return defaultPosition();
  });

  const [prefsScales, setPrefsScales] = useState<number[]>(() =>
    enabledOiNomScaleList(loadOiNomPrefs()),
  );
  const [selectedScales, setSelectedScales] = useState<Record<string, boolean>>(
    {},
  );
  const [bufferKm, setBufferKm] = useState("0");
  const [includeZones, setIncludeZones] = useState(true);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [result, setResult] = useState<NomenclatureIntersectResponse | null>(
    null,
  );

  const persistPanel = useCallback((next: OiNomPanelPrefs) => {
    setPanelPrefs(next);
    saveOiNomPanelPrefs(next);
  }, []);

  useEffect(() => {
    if (!open) return;
    const el = rootRef.current;
    const h = el?.offsetHeight ?? 320;
    setPos(() => {
      if (panelPrefs.x != null && panelPrefs.y != null) {
        return clampPosition(panelPrefs.x, panelPrefs.y, PANEL_WIDTH, h);
      }
      const d = defaultPosition(h);
      return clampPosition(d.x, d.y, PANEL_WIDTH, h);
    });
  }, [open, panelPrefs.x, panelPrefs.y]);

  useEffect(() => {
    const onResize = () => {
      const h = rootRef.current?.offsetHeight ?? 320;
      setPos((p) => clampPosition(p.x, p.y, PANEL_WIDTH, h));
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    const sync = () => {
      const enabled = enabledOiNomScaleList(loadOiNomPrefs());
      setPrefsScales(enabled);
      setSelectedScales((prev) => {
        const next = { ...prev };
        for (const s of enabled) {
          if (next[String(s)] === undefined) next[String(s)] = true;
        }
        return next;
      });
    };
    sync();
    window.addEventListener(OI_NOM_PREFS_EVENT, sync);
    return () => window.removeEventListener(OI_NOM_PREFS_EVENT, sync);
  }, []);

  const activeScales = useMemo(() => {
    const fromSel = OI_NOM_SCALES.filter((s) => selectedScales[String(s)]);
    if (fromSel.length) return fromSel as number[];
    return prefsScales.length ? prefsScales : [500_000];
  }, [selectedScales, prefsScales]);

  const clearResults = useCallback(() => {
    setResult(null);
    onClearHighlight();
  }, [onClearHighlight]);

  const closePanel = useCallback(() => {
    clearResults();
    onOpenChange(false);
    persistPanel({ ...panelPrefs, open: false });
  }, [clearResults, onOpenChange, persistPanel, panelPrefs]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (result) {
        clearResults();
        return;
      }
      closePanel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, result, clearResults, closePanel]);

  const onDragPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    const target = e.target as HTMLElement;
    if (target.closest("button, a, input, [role='checkbox']")) return;
    e.preventDefault();
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    dragRef.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      origX: pos.x,
      origY: pos.y,
    };
  };

  const onDragPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    const h = rootRef.current?.offsetHeight ?? 320;
    const next = clampPosition(
      drag.origX + (e.clientX - drag.startX),
      drag.origY + (e.clientY - drag.startY),
      PANEL_WIDTH,
      h,
    );
    setPos(next);
  };

  const onDragPointerUp = (e: ReactPointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    dragRef.current = null;
    try {
      (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
    const h = rootRef.current?.offsetHeight ?? 320;
    const next = clampPosition(pos.x, pos.y, PANEL_WIDTH, h);
    setPos(next);
    persistPanel({ open: true, x: next.x, y: next.y });
  };

  const runSearch = async () => {
    const km = Number(String(bufferKm).replace(",", "."));
    const buffer_m = Number.isFinite(km) ? Math.max(0, km * 1000) : 0;
    setLoading(true);
    try {
      const data = await nomenclatureSheetsApi.intersect({
        scales: activeScales,
        buffer_m,
        include_zones: includeZones,
      });
      setResult(data);
      onHighlight(data.sheets);
      toast({
        title: "Пересечения найдены",
        description: `Листов: ${data.sheet_count}, объектов: ${data.object_count}`,
      });
    } catch (err) {
      const raw = err instanceof Error ? err.message : String(err);
      const description = raw.length > 280 ? `${raw.slice(0, 280)}…` : raw;
      toast({
        title: "Ошибка поиска",
        description,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const runExport = async () => {
    const km = Number(String(bufferKm).replace(",", "."));
    const buffer_m = Number.isFinite(km) ? Math.max(0, km * 1000) : 0;
    setExporting(true);
    try {
      const blob = await nomenclatureSheetsApi.exportXlsx({
        scales: activeScales,
        buffer_m,
        include_zones: includeZones,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `nomenclature-intersect.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast({
        title: "Ошибка выгрузки",
        description: err instanceof Error ? err.message : String(err),
        variant: "destructive",
      });
    } finally {
      setExporting(false);
    }
  };

  const toggleScale = (scale: number, on: boolean) => {
    setSelectedScales((prev) => ({ ...prev, [String(scale)]: on }));
  };

  if (!open) return null;

  return (
    <div
      ref={rootRef}
      role="dialog"
      aria-label="Пересечения с разграфкой"
      className="pointer-events-auto fixed z-[220] w-[min(22rem,calc(100vw-2rem))] rounded-lg border bg-background/95 shadow-lg backdrop-blur"
      style={{ left: pos.x, top: pos.y }}
    >
      <div
        className={cn(
          "flex cursor-grab items-center gap-1 border-b px-2 py-1.5 active:cursor-grabbing",
          "select-none touch-none",
        )}
        onPointerDown={onDragPointerDown}
        onPointerMove={onDragPointerMove}
        onPointerUp={onDragPointerUp}
        onPointerCancel={onDragPointerUp}
      >
        <GripVertical
          className="h-4 w-4 shrink-0 text-muted-foreground"
          aria-hidden
        />
        <h3 className="min-w-0 flex-1 truncate text-sm font-semibold">
          Пересечения с разграфкой
        </h3>
        <SectionHelpTooltip label="Справка: пересечения с номенклатурой">
          <p>
            Поиск номенклатурных листов (L-36-Г и т.п.), пересекающихся с
            объектами ОИ. Учитываются зоны объекта и буфер.
          </p>
          <ul className="list-disc space-y-1 pl-4">
            <li>Открывается кнопкой в тулбаре (рядом с метровой «Сеткой»).</li>
            <li>Панель можно перетащить за заголовок.</li>
            <li>«Найти» — подсветка листов; «Выгрузить» — Excel.</li>
            <li>Esc: снять подсветку, повторный Esc — закрыть панель.</li>
          </ul>
          <p className="text-muted-foreground">
            Метровая «Сетка» в тулбаре — отдельный ориентир, не номенклатура.
          </p>
        </SectionHelpTooltip>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-7 w-7 shrink-0"
          aria-label="Закрыть панель"
          onClick={closePanel}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      <div className="space-y-3 p-3">
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Масштабы</Label>
          <div className="grid grid-cols-2 gap-1">
            {OI_NOM_SCALES.map((s) => (
              <label
                key={s}
                className="flex cursor-pointer items-center gap-1.5 text-xs"
              >
                <Checkbox
                  checked={Boolean(selectedScales[String(s)])}
                  onCheckedChange={(v) => toggleScale(s, Boolean(v))}
                />
                {formatOiNomScaleLabel(s)}
              </label>
            ))}
          </div>
        </div>

        <div>
          <Label htmlFor="nom-buffer-km" className="text-xs">
            Буфер, км
          </Label>
          <Input
            id="nom-buffer-km"
            type="number"
            min={0}
            step={0.1}
            value={bufferKm}
            onChange={(e) => setBufferKm(e.target.value)}
            className="h-8"
          />
        </div>

        <label className="flex cursor-pointer items-center gap-2 text-xs">
          <Checkbox
            checked={includeZones}
            onCheckedChange={(v) => setIncludeZones(Boolean(v))}
          />
          Учитывать зоны объекта (KLS)
        </label>

        <div className="flex flex-wrap gap-1.5">
          <Button
            type="button"
            size="sm"
            className="h-8 gap-1"
            disabled={loading}
            onClick={() => void runSearch()}
          >
            {loading ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Search className="h-3.5 w-3.5" />
            )}
            Найти
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-8 gap-1"
            disabled={exporting}
            onClick={() => void runExport()}
          >
            {exporting ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Download className="h-3.5 w-3.5" />
            )}
            Выгрузить
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="h-8 gap-1"
            onClick={clearResults}
          >
            Сбросить
          </Button>
        </div>

        {result && (
          <div className="max-h-48 space-y-1 overflow-y-auto text-xs">
            <p className="text-muted-foreground">
              Листов: {result.sheet_count}, объектов: {result.object_count}
            </p>
            {result.sheets.map((sh) => (
              <details
                key={`${sh.scale}-${sh.nomenclature}`}
                className="rounded border p-1.5"
              >
                <summary className="cursor-pointer font-medium">
                  {sh.nomenclature}{" "}
                  <span className="text-muted-foreground">
                    ({formatOiNomScaleLabel(sh.scale)}, {sh.objects.length})
                  </span>
                </summary>
                <ul className="mt-1 space-y-0.5 pl-2">
                  {sh.objects.map((o) => (
                    <li key={o.id}>
                      <button
                        type="button"
                        className="text-left text-primary underline-offset-2 hover:underline"
                        onClick={() => onLocateObject?.(o.id)}
                      >
                        {o.name}
                      </button>
                    </li>
                  ))}
                </ul>
              </details>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
